import { TbCheck, TbCopy } from "solid-icons/tb";
import type { Component } from "solid-js";
import { createSignal, Show } from "solid-js";
import { twMerge } from "tailwind-merge";
import { HighlightedCodeBlock } from "../code-highlighting/HighlightedCodeBlock";
import type { CodeBlockASTNode } from "../toastmark/ast";

interface CodeBlockWithCopyProps {
  node: CodeBlockASTNode;
  class?: string;
  /** Host clipboard writer. Without one the browser API is used, which rejects
   * outside a secure context (chief's wails:// webview), leaving a button that
   * looks like it works and copies nothing. */
  copyText?: (text: string) => Promise<boolean> | boolean;
}

/**
 * Code block component with GitHub-style syntax highlighting and copy-to-clipboard functionality.
 * Features:
 * - GitHub light/dark theme syntax highlighting
 * - Copy button visible on hover
 * - Icon feedback (TbCopy -> TbCheck) with 1.5s timeout
 * - Supports streaming code blocks
 */
export const CodeBlockWithCopy: Component<CodeBlockWithCopyProps> = (props) => {
  const [copied, setCopied] = createSignal(false);

  const handleCopy = async (e: MouseEvent) => {
    e.stopPropagation();
    const code = props.node.literal || "";

    try {
      if (props.copyText) {
        if (!(await props.copyText(code))) return;
      } else {
        await navigator.clipboard.writeText(code);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error("Failed to copy code:", error);
    }
  };

  const containerClass = twMerge("group prism-github prism-github-dark relative", props.class);

  const buttonClass = twMerge(
    "absolute top-2 right-2 z-10",
    "rounded-lg p-1.5",
    "bg-tile hover:bg-tile-hover active:bg-tile-hover",
    "border border-primary-border shadow-sm",
    "transition-all duration-200",
    "focus:ring-2 focus:ring-brand focus:ring-offset-1 focus:outline-none",
    "opacity-0 group-hover:opacity-100"
  );

  return (
    <div class={containerClass}>
      <button
        type="button"
        onClick={(e) => void handleCopy(e)}
        class={buttonClass}
        title={copied() ? "Copied!" : "Copy to clipboard"}
        aria-label={copied() ? "Copied!" : "Copy to clipboard"}
      >
        <Show
          when={copied()}
          fallback={<TbCopy class="size-4 text-secondary-text transition-colors hover:text-primary-text" />}
        >
          <TbCheck class="size-4 text-white" />
        </Show>
      </button>
      <HighlightedCodeBlock node={props.node} class="p-2" />
    </div>
  );
};
